<template>
  <div class="activate-mgmt">
    <div v-if="loadingPlace" class="activate-mgmt__loading">
      <span class="spinner-border text-primary"></span>
      <p class="text-muted mt-4 mb-0">Carregando informações do local…</p>
    </div>

    <template v-else-if="place">
      <section class="activate-mgmt__hero">
        <p class="activate-mgmt__eyebrow">Ativar gestão</p>
        <h1 class="activate-mgmt__headline">Escolha o plano da sua clínica</h1>
        <p class="activate-mgmt__lead">
          O local <strong>{{ place.place_name }}</strong> continua com o mesmo cadastro.
          Você só passa a usar o sistema completo de gestão.
        </p>

        <div class="activate-mgmt__steps">
          <div class="activate-mgmt__step">
            <span class="activate-mgmt__step-num">1</span>
            <span>Confirme o plano</span>
          </div>
          <div class="activate-mgmt__step-divider" aria-hidden="true"></div>
          <div class="activate-mgmt__step">
            <span class="activate-mgmt__step-num">2</span>
            <span>{{ trialDays }} dias de teste grátis</span>
          </div>
          <div class="activate-mgmt__step-divider" aria-hidden="true"></div>
          <div class="activate-mgmt__step">
            <span class="activate-mgmt__step-num">3</span>
            <span>Acesso ao sistema</span>
          </div>
        </div>
      </section>

      <div class="activate-mgmt__trial-banner">
        <div class="activate-mgmt__trial-icon">
          <i class="bi bi-stars"></i>
        </div>
        <div>
          <p class="activate-mgmt__trial-title">{{ trialDays }} dias com acesso completo</p>
          <p class="activate-mgmt__trial-desc">
            Durante o trial você usa o plano Full Free. O plano selecionado abaixo entra em vigor
            ao fim do período de teste.
          </p>
        </div>
      </div>

      <div v-if="loadingPlans" class="activate-mgmt__loading py-10">
        <span class="spinner-border text-primary"></span>
      </div>

      <section v-else class="activate-mgmt__plans">
        <div
          v-for="plan in plans"
          :key="plan.id_platform_plan"
          class="activate-mgmt__plan"
          :class="planCardClasses(plan)"
          :role="plan.is_self_service ? 'button' : undefined"
          :tabindex="plan.is_self_service ? 0 : undefined"
          @click="selectPlan(plan)"
          @keydown.enter.space.prevent="selectPlan(plan)"
        >
          <div v-if="plan.is_recommended" class="activate-mgmt__plan-ribbon">
            Recomendado
          </div>
          <div v-else-if="plan.requires_commercial_contact" class="activate-mgmt__plan-ribbon activate-mgmt__plan-ribbon--enterprise">
            Sob medida
          </div>

          <div v-if="plan.is_self_service" class="activate-mgmt__plan-check" aria-hidden="true">
            <i class="bi bi-check-lg"></i>
          </div>

          <h3 class="activate-mgmt__plan-name">{{ plan.name }}</h3>
          <p class="activate-mgmt__plan-desc">{{ plan.description }}</p>

          <div class="activate-mgmt__plan-price">
            <template v-if="plan.requires_commercial_contact">
              <span class="activate-mgmt__plan-amount activate-mgmt__plan-amount--consult">Sob consulta</span>
            </template>
            <template v-else>
              <span class="activate-mgmt__plan-currency">R$</span>
              <span class="activate-mgmt__plan-amount">{{ formatMoney(plan.price) }}</span>
              <span class="activate-mgmt__plan-period">/mês</span>
            </template>
          </div>

          <div class="activate-mgmt__plan-features flex-grow-1">
            <PlanOfferingPresentation :presentation="plan.presentation" />
          </div>

          <a
            v-if="plan.requires_commercial_contact"
            href="mailto:comercial@ubbimed.com.br?subject=Interesse%20no%20plano%20Enterprise%20Ubbi"
            class="btn btn-sm btn-outline btn-outline-dashed btn-outline-primary w-100 mt-3"
            @click.stop
          >
            <i class="bi bi-envelope me-2"></i>
            Falar com o comercial
          </a>
        </div>
      </section>

      <footer class="activate-mgmt__footer">
        <div class="activate-mgmt__footer-inner">
          <div class="activate-mgmt__footer-text">
            <p class="activate-mgmt__footer-title">
              Plano selecionado:
              <strong>{{ selectedPlan?.name ?? '—' }}</strong>
            </p>
            <p class="activate-mgmt__footer-hint">
              Depois da ativação você configura boleto ou cartão em Minha assinatura.
            </p>
          </div>
          <button
            type="button"
            class="btn btn-primary btn-lg activate-mgmt__cta"
            :disabled="!selectedPlanId || activating"
            @click="activate"
          >
            <span v-if="activating" class="spinner-border spinner-border-sm me-2"></span>
            <template v-else>
              <i class="bi bi-rocket-takeoff me-2"></i>
            </template>
            Ativar gestão e iniciar trial
          </button>
        </div>
      </footer>
    </template>
  </div>
</template>

<script>
import http from '../../helpers/http';
import Alert from '../../helpers/alert';
import { isMarketplacePlace } from '../../helpers/marketplacePlace';
import PlanOfferingPresentation from '../../components/platform_subscription/PlanOfferingPresentation.vue';

export default {
  name: 'ActivateManagementSubscription',
  components: { PlanOfferingPresentation },
  data() {
    return {
      placeId: null,
      place: null,
      plans: [],
      selectedPlanId: null,
      trialDays: 15,
      loadingPlace: true,
      loadingPlans: true,
      activating: false,
    };
  },
  computed: {
    selectedPlan() {
      return this.plans.find((p) => p.id_platform_plan === this.selectedPlanId) ?? null;
    },
  },
  created() {
    this.placeId = this.$route.query.id_place;

    if (!this.placeId) {
      this.$router.replace('/places');
      return;
    }

    this.loadPlace();
    this.loadPlans();
  },
  methods: {
    planCardClasses(plan) {
      return {
        'activate-mgmt__plan--selected': plan.is_self_service && this.selectedPlanId === plan.id_platform_plan,
        'activate-mgmt__plan--recommended': plan.is_recommended,
        'activate-mgmt__plan--enterprise': plan.requires_commercial_contact,
      };
    },
    selectPlan(plan) {
      if (!plan.is_self_service) {
        return;
      }

      this.selectedPlanId = plan.id_platform_plan;
    },
    loadPlace() {
      const userId = this.$store.state.auth.user?.id_user;

      http.get(`/users/${userId}/places`).then((res) => {
        const place = res.data.find((p) => String(p.id_place) === String(this.placeId));

        if (!place) {
          Alert.toastError('Local não encontrado.');
          this.$router.replace('/places');
          return;
        }

        if (!isMarketplacePlace(place)) {
          this.$router.replace('/subscription');
          return;
        }

        this.place = place;
      }).finally(() => {
        this.loadingPlace = false;
      });
    },
    loadPlans() {
      http.get(`/places/${this.placeId}/platform-subscription/plans`).then((res) => {
        this.plans = res.data;
        if (this.plans.length) {
          const recommended = this.plans.find((p) => p.is_recommended);
          const firstSelfService = this.plans.find((p) => p.is_self_service);
          this.selectedPlanId = recommended?.id_platform_plan
            ?? firstSelfService?.id_platform_plan
            ?? null;
        }
      }).finally(() => {
        this.loadingPlans = false;
      });
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2).replace('.', ',');
    },
    async activate() {
      if (!this.selectedPlanId) {
        return;
      }

      this.activating = true;

      try {
        const res = await http.post(
          `/places/${this.placeId}/platform-subscription/convert-to-management`,
          { id_platform_plan: this.selectedPlanId },
        );

        const subscription = res.data.subscription ?? {};
        const place = {
          ...this.place,
          ...res.data.place,
          place_is_marketplace: false,
          platform_subscription_exempt: false,
          platform_subscription_status: subscription.status ?? 'trial',
          platform_subscription_is_blocked: subscription.is_blocked ?? false,
          platform_subscription_can_use: subscription.can_use_platform ?? true,
        };

        await this.$store.dispatch('auth/selectPlace', place);
        this.$store.commit('auth/setPlatformSubscription', subscription);

        Alert.success('Sistema de gestão ativado. Seu período de teste começou agora.');

        if (res.data.subscription?.needs_payment_method) {
          this.$router.push({
            path: '/subscription/payment-method',
            query: { first_subscription: '1' },
          });
        } else {
          this.$router.push('/dashboard');
        }
      } finally {
        this.activating = false;
      }
    },
  },
};
</script>

<style scoped>
.activate-mgmt {
  --ubbi: #780f5a;
  --ubbi-soft: rgba(120, 15, 90, 0.07);
  --ubbi-ring: rgba(120, 15, 90, 0.35);
  max-width: 1100px;
  margin: 0 auto;
  padding-bottom: 7rem;
}

.activate-mgmt__loading {
  text-align: center;
  padding: 4rem 1rem;
}

.activate-mgmt__hero {
  text-align: center;
  margin-bottom: 2rem;
}

.activate-mgmt__eyebrow {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ubbi);
  background: var(--ubbi-soft);
  padding: 0.35rem 0.85rem;
  border-radius: 2rem;
  margin-bottom: 1rem;
}

.activate-mgmt__headline {
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 800;
  color: #181c32;
  margin-bottom: 0.75rem;
  line-height: 1.2;
}

.activate-mgmt__lead {
  font-size: 1.05rem;
  color: #5e6278;
  max-width: 36rem;
  margin: 0 auto 1.75rem;
  line-height: 1.55;
}

.activate-mgmt__steps {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.5rem 0.75rem;
  background: #fff;
  border-radius: 1rem;
  padding: 0.85rem 1.25rem;
  box-shadow: 0 2px 16px rgba(24, 28, 50, 0.06);
}

.activate-mgmt__step {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #3f4254;
}

.activate-mgmt__step-num {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: var(--ubbi);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.activate-mgmt__step-divider {
  width: 1.5rem;
  height: 2px;
  background: #e4e6ef;
  border-radius: 1px;
}

.activate-mgmt__trial-banner {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: linear-gradient(135deg, rgba(120, 15, 90, 0.12) 0%, rgba(120, 15, 90, 0.04) 100%);
  border: 1px solid rgba(120, 15, 90, 0.15);
  border-radius: 1rem;
  padding: 1.15rem 1.35rem;
  margin-bottom: 2rem;
}

.activate-mgmt__trial-icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  background: var(--ubbi);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.activate-mgmt__trial-title {
  font-weight: 700;
  color: #181c32;
  margin: 0 0 0.25rem;
  font-size: 1rem;
}

.activate-mgmt__trial-desc {
  margin: 0;
  font-size: 0.9rem;
  color: #5e6278;
  line-height: 1.5;
}

.activate-mgmt__plans {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  align-items: stretch;
}

@media (min-width: 768px) {
  .activate-mgmt__plans {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 992px) {
  .activate-mgmt__plans {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1200px) {
  .activate-mgmt__plans {
    grid-template-columns: repeat(4, 1fr);
  }
}

.activate-mgmt__plan {
  position: relative;
  background: #fff;
  border-radius: 1.15rem;
  padding: 1.5rem 1.35rem 1.35rem;
  border: 2px solid #ebedf3;
  box-shadow: 0 4px 20px rgba(24, 28, 50, 0.05);
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  outline: none;
  display: flex;
  flex-direction: column;
}

.activate-mgmt__plan:hover {
  transform: translateY(-4px);
  border-color: var(--ubbi-ring);
  box-shadow: 0 12px 32px rgba(120, 15, 90, 0.12);
}

.activate-mgmt__plan--selected {
  border-color: var(--ubbi);
  box-shadow: 0 12px 36px rgba(120, 15, 90, 0.18);
}

.activate-mgmt__plan--recommended {
  border-color: rgba(120, 15, 90, 0.35);
}

.activate-mgmt__plan--enterprise {
  border-style: dashed;
  cursor: default;
}

.activate-mgmt__plan--enterprise:hover {
  transform: none;
}

.activate-mgmt__plan--recommended.activate-mgmt__plan--selected {
  transform: translateY(-4px);
}

.activate-mgmt__plan-ribbon--enterprise {
  background: #7239ea;
}

.activate-mgmt__plan-amount--consult {
  font-size: 1.35rem;
  color: #3f4254;
}

.activate-mgmt__plan-ribbon {
  position: absolute;
  top: -0.65rem;
  left: 50%;
  transform: translateX(-50%);
  background: var(--ubbi);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.3rem 0.85rem;
  border-radius: 2rem;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(120, 15, 90, 0.3);
}

.activate-mgmt__plan-check {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 1.65rem;
  height: 1.65rem;
  border-radius: 50%;
  border: 2px solid #d8d8e5;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: transparent;
  font-size: 1rem;
  font-weight: 700;
  transition: all 0.15s ease;
}

.activate-mgmt__plan--selected .activate-mgmt__plan-check {
  background: var(--ubbi);
  border-color: var(--ubbi);
  color: #fff;
}

.activate-mgmt__plan-name {
  font-size: 1.35rem;
  font-weight: 800;
  color: #181c32;
  margin: 0.5rem 0 0.5rem;
  padding-right: 2rem;
}

.activate-mgmt__plan-desc {
  font-size: 0.875rem;
  color: #7e8299;
  line-height: 1.45;
  margin-bottom: 1.25rem;
  flex-grow: 1;
}

.activate-mgmt__plan-price {
  display: flex;
  align-items: baseline;
  gap: 0.15rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px dashed #eff2f5;
}

.activate-mgmt__plan-currency {
  font-size: 1rem;
  font-weight: 700;
  color: #5e6278;
}

.activate-mgmt__plan-amount {
  font-size: 2.25rem;
  font-weight: 800;
  color: var(--ubbi);
  line-height: 1;
  letter-spacing: -0.02em;
}

.activate-mgmt__plan-period {
  font-size: 0.95rem;
  color: #a1a5b7;
  font-weight: 500;
}

.activate-mgmt__plan-features {
  margin: 0;
  padding: 0;
}

.activate-mgmt__feature-group {
  margin-bottom: 1rem;
}

.activate-mgmt__feature-group-title {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #7e8299;
  margin-bottom: 0.5rem;
}

.activate-mgmt__feature-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.activate-mgmt__feature-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #3f4254;
  margin-bottom: 0.65rem;
  line-height: 1.4;
}

.activate-mgmt__feature-list li i {
  color: #50cd89;
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.activate-mgmt__footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 90;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-top: 1px solid #ebedf3;
  box-shadow: 0 -8px 30px rgba(24, 28, 50, 0.08);
}

.activate-mgmt__footer-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: 1rem 1.25rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.activate-mgmt__footer-title {
  margin: 0 0 0.2rem;
  font-size: 0.95rem;
  color: #181c32;
}

.activate-mgmt__footer-hint {
  margin: 0;
  font-size: 0.8rem;
  color: #7e8299;
}

.activate-mgmt__cta {
  font-weight: 700;
  padding-left: 1.75rem;
  padding-right: 1.75rem;
  background-color: var(--ubbi) !important;
  border-color: var(--ubbi) !important;
}

.activate-mgmt__cta:hover:not(:disabled) {
  background-color: #5e0c46 !important;
  border-color: #5e0c46 !important;
}

@media (max-width: 575px) {
  .activate-mgmt__step-divider {
    display: none;
  }

  .activate-mgmt__steps {
    flex-direction: column;
    align-items: stretch;
    text-align: left;
  }

  .activate-mgmt__footer-inner {
    flex-direction: column;
    align-items: stretch;
  }

  .activate-mgmt__cta {
    width: 100%;
  }
}
</style>
